import { camelizeKeys } from 'humps'
import {
  ERROR_MESSAGE_LIST,
  GENERIC_ERROR_MESSAGE,
  DEFAULT_PER_PAGE,
  LOCATION_TYPES,
  REPORT_TYPES,
  SYSTEM_ALERT_TYPES,
} from '../constants'

const unique = (x, idx, self) => self.indexOf(x) === idx

const existy = x => x != null

const groupBy = (array, iterator) =>
  array.reduce((mem, x) => {
    const value = iterator(x)

    mem[value] = mem[value] || []
    mem[value].push(x)

    return mem
  }, {})

const debounce = (func, wait, immediate) => {
  let timeout

  const debounced = function() {
    const context = this,
      args = arguments
    const later = function() {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    const callNow = immediate && !timeout

    clearTimeout(timeout)

    timeout = setTimeout(later, wait)

    if (callNow) func.apply(context, args)
  }

  debounced.cancel = () => {
    clearTimeout(timeout)
    timeout = null
  }

  return debounced
}

const normalizeAmbientLightReading = dataObj => {
  const { ambientLight, ...rest } = dataObj
  return ambientLight
    ? Object.assign({ ambientLight: ambientLight / 1000 }, rest)
    : dataObj
}

const getPathSnippets = pathname => pathname.split('/').filter(x => x)
const getPathSnippetAtIndex = (pathname, index) =>
  getPathSnippets(pathname)[index]

// flask-restful reports request argument errors (e.g. an out-of-range
// lat/lng) as { message: { field_name: 'reason' } }; returns those as
// camelCase form field errors, or null for any other error shape
const pickFieldErrors = ({ apiError = {} } = {}) => {
  const { message } = apiError

  if (!message || typeof message !== 'object' || Array.isArray(message)) {
    return null
  }

  return camelizeKeys(message)
}

const pickErrorMessage = ({ apiError = {} }) => {
  const fieldErrors = pickFieldErrors({ apiError })

  if (fieldErrors) {
    return Object.values(fieldErrors).join(' ')
  }

  const index = ERROR_MESSAGE_LIST.map(x => x.apiKey).indexOf(
    apiError.message || apiError.status
  )
  const errorMessage = ERROR_MESSAGE_LIST[index]

  if (index > -1) {
    if (errorMessage.duration) {
      return errorMessage
    } else {
      return errorMessage.message
    }
  } else {
    return GENERIC_ERROR_MESSAGE
  }
}

const configurePagination = ({
  perPage = DEFAULT_PER_PAGE,
  page,
  total,
  onShowSizeChange,
}) => ({
  onShowSizeChange,
  pageSizeOptions: [25, 50, 100],
  showSizeChanger: total > 25,
  position: ['topRight', 'bottomRight'],
  defaultPageSize: perPage,
  current: page,
  total,
  size: 'small',
})

const escapeRegExp = string => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const isReportTypeDisabled = (reportType, hasLocationGroups) => {
  if (hasLocationGroups) {
    switch (reportType) {
      case REPORT_TYPES.FLOOR_LOCATIONS_PER_GRAPH:
      case REPORT_TYPES.FLOOR_AVG_PER_GRAPH:
      case REPORT_TYPES.ALL_FLOORS_AVG_PER_GRAPH:
        return true
    }
  } else {
    return reportType === REPORT_TYPES.LOCATION_GROUPS_PER_GRAPH
  }
}

// the device an alert is on, as shown in alert names
const alertDeviceName = ({ assetType, flowMonitorPillarId }) => {
  if (assetType === LOCATION_TYPES.PRESSURE_MONITOR) return 'Pressure Monitor'
  if (assetType === LOCATION_TYPES.FLOW_MONITOR || flowMonitorPillarId) {
    return 'Water Monitor'
  }
  return 'Smart Pod'
}

// display name for an alert: its threshold's name, or for a system alert
// (no threshold) a name from its system alert type and device
const getAlertName = (alert = {}) => {
  const { thresholdName, systemAlertType } = alert
  if (thresholdName) return thresholdName
  switch (systemAlertType) {
    case SYSTEM_ALERT_TYPES.ANOMALY:
      return 'Anomaly'
    case SYSTEM_ALERT_TYPES.WET_POD:
      return 'Wet Smart Pod'
    case SYSTEM_ALERT_TYPES.THRESHOLD_EXCEEDED:
      return `${alertDeviceName(alert)} Threshold Exceeded`
    default:
      return `${alertDeviceName(alert)} Offline`
  }
}

// whether an alert is an offline alert (shown with the power-off icon)
const isOfflineAlert = ({ thresholdName, systemAlertType } = {}) =>
  !thresholdName &&
  (!systemAlertType || systemAlertType === SYSTEM_ALERT_TYPES.OFFLINE)

export {
  getAlertName,
  isOfflineAlert,
  unique,
  existy,
  groupBy,
  debounce,
  getPathSnippets,
  getPathSnippetAtIndex,
  normalizeAmbientLightReading,
  pickErrorMessage,
  pickFieldErrors,
  configurePagination,
  escapeRegExp,
  isReportTypeDisabled,
}
